import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';

import { ApiError, claimToken, type ClaimResult } from '../api';

type Status = 'loading' | 'success' | 'expired' | 'invalid' | 'no_offer' | 'error';

export default function ClaimPage() {
  const { token = '' } = useParams<{ token: string }>();
  const brandingRef = useRef<HTMLDivElement>(null);
  const [status, setStatus] = useState<Status>('loading');
  const [data, setData] = useState<ClaimResult | null>(null);

  useEffect(() => {
    if (!token) {
      setStatus('invalid');
      return;
    }
    let cancelled = false;
    claimToken(token)
      .then((result) => {
        if (cancelled) return;
        setData(result);
        setStatus('success');
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        if (err instanceof ApiError) {
          if (err.status === 410) {
            setStatus('expired');
            return;
          }
          if (err.status === 404) {
            setStatus('invalid');
            return;
          }
          if (err.status === 422) {
            setStatus('no_offer');
            return;
          }
        }
        setStatus('error');
      });
    return () => {
      cancelled = true;
    };
  }, [token]);

  // Apply venue brand colors when data arrives.
  useEffect(() => {
    const el = brandingRef.current;
    if (!el || !data?.venue) return;
    if (data.venue.background_color)
      el.style.setProperty('--venue-bg', data.venue.background_color);
    if (data.venue.foreground_color)
      el.style.setProperty('--venue-fg', data.venue.foreground_color);
  }, [data]);

  if (status === 'loading') {
    return (
      <main className="container" aria-busy="true">
        <div className="spinner" role="status">
          <span className="sr-only">Setting up your card…</span>
        </div>
        <p>Setting up your card…</p>
      </main>
    );
  }

  if (status === 'expired') {
    return (
      <main className="container">
        <h1>Link expired</h1>
        <p>This link is no longer valid — magic links expire after 15 minutes.</p>
        <p>Scan the QR code at the venue to get a new one.</p>
      </main>
    );
  }

  if (status === 'invalid') {
    return (
      <main className="container">
        <h1>Invalid link</h1>
        <p>This link isn&apos;t valid. Try scanning the QR code at the venue again.</p>
      </main>
    );
  }

  if (status === 'no_offer') {
    return (
      <main className="container">
        <h1>No active offer</h1>
        <p>This venue doesn&apos;t have an active offer right now. Check back soon.</p>
      </main>
    );
  }

  if (status === 'error') {
    return (
      <main className="container">
        <h1>Something went wrong</h1>
        <p>
          We couldn&apos;t set up your card. Try tapping the link in your text message again, or ask
          the bartender for help.
        </p>
      </main>
    );
  }

  // success
  const d = data!;
  return (
    <div ref={brandingRef} className="claim-page">
      {d.venue.logo_url && (
        <header className="venue-header">
          <img src={d.venue.logo_url} alt={d.venue.name} className="venue-logo" />
        </header>
      )}
      <main className="container">
        <h1>{d.offer.title}</h1>
        <p className="offer-body">{d.offer.body}</p>

        <div className="wallet-cta">
          <a href={d.card_download_url} className="add-to-wallet" aria-label="Add to Apple Wallet">
            <span className="add-to-wallet__apple" aria-hidden="true"></span>
            Add to Apple Wallet
          </a>
        </div>

        <p className="instructions">{d.instructions}</p>
        <p className="text-muted">{d.sms_backup_note}</p>

        <p className="fine-print">
          {d.offer.terms && <>{d.offer.terms} &mdash; </>}
          <a href="/legal/consent">Terms &amp; privacy</a>
        </p>
      </main>
    </div>
  );
}
